import { makeStyles } from 'tss-react/mui';
import { Theme, Typography } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Routes, { routes } from '../Routes';
import { ApplicationState, LastAction } from '../store';
import { ConfigState } from '../store/config/types';
import { colors } from '../theme';
import SideMenu from '../containers/sidemenu/SideMenu';
import NavBar from '../components/navbar/NavBar';
import Button from '../components/button/Button';
import AppFooter from '../containers/footer/AppFooter';
import { useSnackbar } from 'notistack';
import { CeclApiState, UserTypes } from '../store/ceclapi/types';
import UserInfoDropdown from '../containers/userinfodropdown/UserInfoDropdown';
import { cookiesRequest, saveSnackbarKey } from '../store/config/actions';
import { useNavigate, useLocation } from 'react-router-dom';

const useStyles = makeStyles()((theme: Theme) =>
    ({
        pages: {
            display: 'flex',
            height: 'calc(100% - 64px)',
        },

        titleBar: {
            backgroundColor: colors.white,
            padding: theme.spacing(3),
            display: 'flex',
        },

        fixed: {
            position: 'fixed',
        },

        title: {
            flexGrow: 1,
        },

        titleRight: {
            flexGrow: 1,
            textAlign: 'right',
        },

        button: {
            margin:  theme.spacing(3)
        },

        main: {
            flexGrow: 1,
            display: 'flex',
        },

        content: {
            backgroundColor: colors.lightGray,
            zIndex: -1,
            padding: theme.spacing(3),
            paddingTop: theme.spacing(11),
            overflowX: 'hidden',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            zIndex: 1,
        }
    }));

const Pages: React.FC = () => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const config = useSelector<ApplicationState, ConfigState>((state) => state.config);
    const cecl = useSelector<ApplicationState, CeclApiState>((state) => state.cecl);
    const lastAction = useSelector<ApplicationState, LastAction>((state) => state.lastAction);

    // Sort the routes from biggest to smallest. Sorting based on the path before the first parameter.
    // This is this is done to make sure that the most specific route is matched first. 
    // Example: /forecast/view/ vs /forecast/
    const sortedRoutes = React.useMemo(() => 
        [...routes].sort((a, b) => {
            const aPrefix = a.path.split(':')[0];
            const bPrefix = b.path.split(':')[0];
            return bPrefix.length - aPrefix.length;
        }), 
        [routes]
    );
    const currentPage = sortedRoutes.find((r) => location.pathname.startsWith(r.path.split(':')[0]));
    const currentPageParent = routes.find((r) => location.pathname.startsWith(r.path));

    const { enqueueSnackbar } = useSnackbar();

    // This functions as a redux action watcher.
    React.useEffect(() => {
        if (lastAction.type === '@@config/DISPLAY_TOAST') {
            const { message, variant, persist, anchorOrigin, action } = lastAction.payload;
            const snackbarKey = enqueueSnackbar(message, { variant, persist, style: {
                whiteSpace: 'pre-line'
            }, anchorOrigin: anchorOrigin, action});
            dispatch(saveSnackbarKey(snackbarKey));
        }

        if (lastAction.type === '@@config/COOKIES_SUCCESS' && location.pathname !== '/auth' && config.isLoggedIn) {
            navigate('/auth', { state: { url: location.pathname, page: 'root' } });
        }
    }, [lastAction]);

    React.useEffect(() => {
        if (!config.isLoggedIn) {
            dispatch(cookiesRequest());
        }
    }, []);

    let navBarTitle = currentPage?.name;
    if (currentPage?.name && currentPage?.name.toUpperCase() === "DASHBOARD" && (cecl.user?.givenName || cecl.user?.name)){
        navBarTitle = "Welcome, " + (cecl.user?.givenName ?? cecl.user?.name?.split(' ')[0]) + "!";
    } 

    return (
        <>
            {currentPageParent?.order === -1 ? (
                <Routes />
            ) : (
                <>
                    <NavBar className={classes.fixed} title={navBarTitle}>

                    {  cecl.user?.userType !== UserTypes.Subscribed &&  (
                            <Button className={classes.button} onClick={() => navigate('/scheduledemo/')}>Subscribe</Button>
                        ) }

                        {currentPage?.name === 'Dashboard' && cecl.user?.userType === UserTypes.Subscribed ? (
                            <Button onClick={() => navigate(`/forecast/assumptions/${cecl.user!.routingNumber!}`)}>Create New Forecast</Button>
                        ) : (
                            <Typography>{cecl.user?.creditUnionName}</Typography>
                        )}                                              

                        <UserInfoDropdown />
                    </NavBar>
                    <div className={classes.pages}>
                        {/* TODO: fix CSS for persistent hidden side menu */}
                        <SideMenu open={config.sideMenu} title="[CECL Logo]" loggedIn={cecl.user?.isAuthenticated} />
                        <div className={classes.content}>
                            <Routes />
                        </div>
                    </div>
                    <AppFooter />
                </>
            )}
        </>
    );
};

export default Pages;
